import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Plus, MessagesSquare, ClipboardPlus, Loader2, HardHat } from "lucide-react";
import { API, useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/Me";

const MODES = [
  { k: "rapido", label: "Rápido", hint: "GPT 6 Luna · respostas rápidas" },
  { k: "equilibrado", label: "Equilibrado", hint: "GPT 5.6 Terra · dia a dia" },
  { k: "avancado", label: "Avançado", hint: "GPT 6 Sol · pensa e calcula mais" },
  { k: "especialista", label: "Especialista", hint: "GPT 6 Astra · casos difíceis, mais lento" },
];
const SUGGEST = ["Que tinta uso no banheiro?", "Quantos sacos de argamassa para 10 m² de piso?", "Disjuntor desarma sozinho, é perigoso?", "Como saber se um orçamento é justo?"];

function Bubble({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      {!mine && <div className="mr-2 mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#FFC700] text-[#111317]"><HardHat size={16} /></div>}
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-relaxed ${mine ? "whitespace-pre-wrap bg-[#FFC700] text-[#111317]" : "md border border-white/10 bg-[#181B20] text-slate-200"}`}>{!m.content ? <Loader2 size={16} className="animate-spin" /> : mine ? m.content : <ReactMarkdown>{m.content}</ReactMarkdown>}</div>
    </div>
  );
}

export default function Assistant() {
  const api = useApi();
  const nav = useNavigate();
  const { me, country } = useMe();
  const [convs, setConvs] = useState([]);
  const [cid, setCid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(() => localStorage.getItem("assistant_mode") || "equilibrado");
  const end = useRef();
  const pickMode = (k) => { setMode(k); localStorage.setItem("assistant_mode", k); };

  const loadConvs = () => api.get("/ai/conversations").then((r) => setConvs(r.data)).catch(() => {});
  useEffect(() => { loadConvs(); }, [api]); // eslint-disable-line
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const open = async (id) => { setCid(id); const { data } = await api.get(`/ai/conversations/${id}`); setMsgs(data.messages); };

  const send = async (t = text) => {
    if (!t.trim() || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: t }, { role: "assistant", content: "" }]);
    try {
      const token = await api.getToken();
      const res = await fetch(`${API}/ai/chat`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ conversation_id: cid, message: t, mode, country }) });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw { response: { data: j } }; }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const d = p.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          const j = JSON.parse(d);
          if (j.conversation_id) setCid(j.conversation_id);
          if (j.error) toast.error(j.error);
          if (j.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + j.delta }; return c; });
        }
      }
      loadConvs();
    } catch (e) { toast.error(errMsg(e)); setMsgs((m) => m.slice(0, -1)); }
    setBusy(false);
  };

  const toJob = async () => {
    if (!me?.profile_type) return nav("/cadastro");
    try { const { data } = await api.post(`/ai/conversations/${cid}/to-job`); nav("/chamados/novo", { state: data }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mx-auto grid h-[calc(100vh-70px-76px)] max-w-7xl gap-4 px-4 py-4 sm:px-6 md:h-[calc(100vh-70px)] md:grid-cols-[260px_1fr]">
      <aside className="panel hidden flex-col overflow-hidden md:flex">
        <button data-testid="assistant-new-chat" className="btn-hazard m-3" onClick={() => { setCid(null); setMsgs([]); }}><Plus size={18} />Nova conversa</button>
        <div className="flex-1 space-y-1 overflow-y-auto px-2 pb-3">
          {convs.map((c) => (
            <button key={c.id} data-testid={`assistant-conv-${c.id}`} onClick={() => open(c.id)} className={`w-full truncate rounded-lg px-3 py-2 text-left text-sm ${cid === c.id ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5"}`}>{c.title}</button>
          ))}
        </div>
      </aside>
      <section className="panel flex min-h-0 flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2"><MessagesSquare className="text-[#FFC700]" size={20} /><span className="font-display text-xl font-bold uppercase">Assistente Zelobra</span></div>
          <div className="flex gap-2">
            {me?.profile_type !== "pro" && cid && msgs.length > 1 && <button data-testid="assistant-to-job" onClick={toJob} className="btn-hazard px-3 py-2 text-sm"><ClipboardPlus size={16} />Virar chamado</button>}
            <button data-testid="assistant-new-chat-mobile" className="btn-ghost px-3 py-2 text-sm md:hidden" onClick={() => { setCid(null); setMsgs([]); }}><Plus size={16} /></button>
          </div>
        </div>
        <div className="flex gap-1.5 overflow-x-auto border-b border-white/10 px-3 py-2" data-testid="assistant-modes">
          {MODES.map((m) => (
            <button key={m.k} type="button" title={m.hint} data-testid={`assistant-mode-${m.k}`} onClick={() => pickMode(m.k)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${mode === m.k ? "bg-[#FFC700] text-[#111317]" : "border border-white/15 text-slate-300 hover:border-white/40"}`}>{m.label}</button>
          ))}
          <span className="hidden items-center whitespace-nowrap pl-2 text-xs text-slate-500 sm:flex" data-testid="assistant-mode-hint">{MODES.find((m) => m.k === mode)?.hint}</span>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="assistant-messages">
          {!msgs.length && (
            <div className="mx-auto max-w-lg py-8 text-center">
              <h2 className="text-3xl font-black uppercase">Pergunte qualquer coisa sobre a sua obra</h2>
              <p className="mt-2 text-sm text-slate-400">Materiais, quantidades, riscos, orçamentos. Limite de 30 mensagens por dia.</p>
              <div className="mt-6 grid gap-2 sm:grid-cols-2">{SUGGEST.map((s, i) => <button key={s} data-testid={`assistant-suggest-${i}`} onClick={() => send(s)} className="rounded-xl border border-white/10 px-4 py-3 text-left text-sm text-slate-300 hover:border-[#FFC700]/60">{s}</button>)}</div>
            </div>
          )}
          {msgs.map((m, i) => <Bubble key={i} m={m} />)}
          <div ref={end} />
        </div>
        <form className="flex gap-2 border-t border-white/10 p-3" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <input data-testid="assistant-input" className="field" value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreva sua dúvida…" />
          <button data-testid="assistant-send" className="btn-hazard px-4" disabled={busy || !text.trim()}>{busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}</button>
        </form>
      </section>
    </div>
  );
}
